import { useEffect, useState, type ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Award, Book, BookOpen, Home, Map, Medal, Menu, Mic, Route, Trophy, Users } from 'lucide-react';
import { useManagedBy, useMyChildren } from '@core/firebase/children';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { RoleSwitcher, type AppRole } from '@/components/RoleSwitcher';
import { Sidebar, type NavEntry } from '@/components/Sidebar';
import { UserMenu } from '@/components/UserMenu';
import { Brand } from '@/components/Brand';
import { ThemeToggle } from '@/components/ThemeToggle';
import { ADMIN_SECTIONS, AdminSectionContext, sectionForPath } from '@/lib/adminSections';

/** Where each role begins. Switching roles is navigation, nothing more. */
const ROLE_HOME: Record<AppRole, string> = {
  parent: '/children',
  student: '/home',
  muqri: '/session/today',
  admin: '/admin/requests',
};

/** The pages that belong to each half of the product. */
const ROLE_NAV: Record<AppRole, NavEntry[]> = {
  student: [
    { testId: 'nav-home', to: '/home', labelKey: 'student.home', icon: Home },
    // Before the tracks: a pupil's own syllabus is the thing they are set,
    // and a track is something they choose on top of it.
    { testId: 'nav-curriculum', to: '/curriculum', labelKey: 'curriculum.nav', icon: Book },
    // المرحلة 5, in the order a child uses them: the map says what is next,
    // the shelf says what it is worth, the board is where they stand.
    { testId: 'nav-map', to: '/map', labelKey: 'game.map.nav', icon: Map },
    // Practice sits under the map: it is the same āya the map points at.
    { testId: 'nav-practice', to: '/practice', labelKey: 'practice.nav', icon: Mic },
    { testId: 'nav-rewards', to: '/rewards', labelKey: 'game.store.nav', icon: Trophy },
    { testId: 'nav-board', to: '/board', labelKey: 'game.board.nav', icon: Medal },
    { testId: 'nav-certificates', to: '/certificates', labelKey: 'certificates.nav', icon: Award },
    { testId: 'nav-tracks', to: '/tracks', labelKey: 'tracks.nav', icon: Route },
    { testId: 'nav-request-muqri', to: '/request-muqri', labelKey: 'muqriRequest.nav', icon: BookOpen },
  ],
  // والد — a portal of its own since 2026-10-15, not a row on the pupil side.
  parent: [
    { testId: 'nav-children', to: '/children', labelKey: 'children.nav', icon: Users },
  ],
  muqri: [
    { testId: 'nav-session', to: '/session/today', labelKey: 'muqri.session', icon: Mic },
    // المعمل sits beside the session: a معلم running a class lab is in a
    // session, and moves between the two constantly.
    { testId: 'nav-lab', to: '/lab', labelKey: 'lab.nav', icon: Users },
  ],
  // Four categories (#12); each is active on every page of its group.
  admin: ADMIN_SECTIONS.map(({ testId, labelKey, icon, pages }) => ({
    testId, labelKey, icon, to: pages[0].to, match: pages.map((p) => p.to),
  })),};

const COLLAPSE_KEY = 'fusilat-sidebar-collapsed';
/** The portal used last, so the site reopens there (owner, 2026-10-15). */
export const LAST_PORTAL_KEY = 'fusilat-last-portal';
/** Restoring happens once per tab, not on every visit to /home. */
const RESTORED_KEY = 'fusilat-portal-restored';

/**
 * Which half of the product this path belongs to.
 *
 * Derived from the URL rather than stored, exactly as the mobile app derives
 * it from the route: the role you are acting as IS the page you are on, so
 * there is no state to persist and nothing that can disagree with what is in
 * front of you.
 */
export function roleForPath(pathname: string): AppRole {
  if (pathname.startsWith('/admin')) return 'admin';
  if (pathname.startsWith('/session') || pathname.startsWith('/lab')) return 'muqri';
  if (pathname.startsWith('/children')) return 'parent';
  return 'student';
}

/**
 * The shell every signed-in page sits in — the Saleem apps' layout in the
 * redesign's dress (DESIGN.md): a sidebar with the logo, the sections and the
 * person, that collapses to an icon rail on a wide screen and becomes a drawer
 * on a narrow one; and a light top bar holding the labelled role switch and
 * the sun/moon toggle in one pill.
 *
 * It shows ONE role's pages at a time; the switcher is how you reach the
 * others. Listing every role's links together would make the portal a
 * permanent fixture of a طالب's chrome, which is the opposite of how the
 * phone app works and of how rare the admin role is.
 */
export function AppShell({ children }: { children: ReactNode }) {
  const { identity, isMuqri, isAdmin, loading } = useAuth();
  const { t } = useLanguage();
  const { pathname } = useLocation();
  const navigate = useNavigate();

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [collapsed, setCollapsed] = useState<boolean>(() => {
    try {
      return localStorage.getItem(COLLAPSE_KEY) === '1';
    } catch {
      // A private window can refuse storage outright; expanded is the default.
      return false;
    }
  });

  const toggleCollapse = () => {
    setCollapsed((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(COLLAPSE_KEY, next ? '1' : '0');
      } catch {
        // Losing the preference on the next visit is the whole cost.
      }
      return next;
    });
  };

  /*
   * A child holding the device is not a guardian. أبنائي creates accounts for
   * children and sets the guardian PIN; offering it to a nine-year-old was
   * exactly what the live walk found after the hand-over. The pages refuse
   * her too — this only stops the app inviting her there.
   */
  const { managedBy } = useManagedBy(identity?.userId ?? null);
  const { children: myChildren, loading: childrenLoading } = useMyChildren(identity?.userId ?? null);
  // والد = has at least one child, and is not a managed child.
  const isParent = myChildren.length > 0 && !managedBy;

  // A drawer left open over the page it just navigated to hides the answer.
  useEffect(() => { setDrawerOpen(false); }, [pathname]);

  /*
   * Almost everyone is a طالب and nothing else, and the switcher renders
   * nothing for a single role — so this is not a control most people see. An
   * approved معلم is the exception, and an admin is a طالب who ALSO holds
   * the portal: the switcher is how they reach it, since there is no separate
   * sign-in and no tab for it in the student chrome.
   */
  const available: AppRole[] = loading ? ['student'] : (['parent', 'muqri', 'student', 'admin'] as AppRole[]).filter((role) => (
    role === 'student' || (role === 'parent' && isParent) || (role === 'muqri' && isMuqri) || (role === 'admin' && isAdmin)
  ));

  // /children before the first child is added belongs to no portal yet: it
  // stays on the pupil side, where the way in is.
  const pathRole = roleForPath(pathname);
  const current: AppRole = available.includes(pathRole) ? pathRole : 'student';

  /*
   * The pupil side: an approved معلم is not invited to become one, and an
   * account with no child yet is offered أبنائي to add the first (after that
   * the children are the والد portal). The named door into the teacher side
   * that App Review asked for is now the labelled role button.
   */
  const pupil = ROLE_NAV.student
    .filter((e) => !(isMuqri && e.testId === 'nav-request-muqri'))
    .concat(!managedBy && !childrenLoading && myChildren.length === 0
      ? [{ testId: 'nav-children', to: '/children', labelKey: 'children.nav', icon: Users }]
      : []);
  const entries = current === 'student' ? pupil : ROLE_NAV[current];

  const switchTo = (role: AppRole) => {
    try { localStorage.setItem(LAST_PORTAL_KEY, role); } catch { /* storage refused: fine */ }
    navigate(ROLE_HOME[role]);
  };

  // Reopen the portal used last — once per tab, after the roles are known.
  useEffect(() => {
    if (loading || childrenLoading || pathname !== '/home') return;
    try {
      if (sessionStorage.getItem(RESTORED_KEY)) return;
      sessionStorage.setItem(RESTORED_KEY, '1');
      const last = localStorage.getItem(LAST_PORTAL_KEY) as AppRole | null;
      if (last && last !== 'student' && available.includes(last)) navigate(ROLE_HOME[last], { replace: true });
    } catch { /* storage refused: stay on /home */ }
  }, [loading, childrenLoading, pathname, available.join(','), navigate]);

  const hasRoles = available.length > 1;

  return (
    // clip, not hidden: the off-screen drawer must never widen the page, and
    // clip keeps the sticky top bar working.
    <div className="min-h-screen overflow-x-clip">
      <Sidebar
        entries={entries}
        open={drawerOpen}
        setOpen={setDrawerOpen}
        collapsed={collapsed}
        toggleCollapse={toggleCollapse}
        // Identity, appearance, language and sign-out in one control at the
        // foot of the column, so the chrome does not sprout siblings.
        footer={<UserMenu collapsed={collapsed} />}
      />

      {/* Logical padding, so the reserved column follows the reading side. */}
      <div className={collapsed
        ? 'transition-all duration-300 lg:ps-[76px]'
        : 'transition-all duration-300 lg:ps-64'}>
        <header className="sticky top-0 z-30 flex h-16 items-center justify-between gap-3 bg-ground/85 px-4 backdrop-blur-md lg:h-20 lg:bg-transparent lg:px-12 lg:backdrop-blur-none">
          {/* The sidebar carries the logo on a wide screen; a narrow one has
              only this bar until the drawer is opened. */}
          <div className="flex min-w-0 items-center gap-1 lg:hidden">
            <button
              type="button"
              data-testid="open-sidebar"
              onClick={() => setDrawerOpen(true)}
              aria-label={t('nav.menu')}
              className="flex h-10 w-10 items-center justify-center rounded-full text-ink hover:bg-surface"
            >
              <Menu className="h-5 w-5" strokeWidth={1.8} />
            </button>
            {/* A phone bar holding the role switch has no room for the motto
                beside the mark; the switch pushed the page sideways. */}
            <Brand size="sm" mottoClassName={hasRoles ? 'hidden sm:inline' : ''} />
          </div>

          {/* «طالب | معلم» and the sun/moon in ONE pill, at the end of the bar. */}
          <div
            data-testid="header-pill"
            className="card-soft ms-auto flex shrink-0 items-center gap-1.5 rounded-full p-1"
          >
            <RoleSwitcher
              available={available}
              current={current}
              onSelect={switchTo}
            />
            {hasRoles && <span aria-hidden="true" className="mx-1 h-5 w-px bg-hair" />}
            <ThemeToggle />
          </div>
        </header>

        <main className="px-4 pb-10 pt-2 lg:px-12 lg:pt-0">
          <AdminSectionContext.Provider value={sectionForPath(pathname)}>{children}</AdminSectionContext.Provider>
        </main>
      </div>
    </div>
  );
}
